import React, { Component, useRef, useEffect } from 'react'
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom'
import { SwitchTransition, Transition } from 'react-transition-group'
import { throttle } from 'lodash';
import gsap from 'gsap'
import Header from '../components/header/Header'
import Social from '../components/pageContent/Social'
import CustomCursor from '../components/cursor/CustomCursor'

import { getViewportSize } from '../utils/screen'

import AnimatedWrapper from "./AnimatedWrapper";

import Landing from './pages/Landing'
import Work from './pages/Work'
import Timeline from './pages/Timeline'
import About from './pages/About'
import Casestudy from './pages/Casestudy'

import NoMatch from './pages/NoMatch'

import Theme from '../constants/theme'

import './App.css';

// Global mask ref that persists across page transitions
let globalMaskRef = null;
let introRevealCallback = null;
let introRevealPending = false;

export const setGlobalMaskRef = (ref) => {
        globalMaskRef = ref;
};

export const getGlobalMask = () => globalMaskRef;

export const setIntroRevealCallback = (callback) => {
        
        introRevealCallback = callback;
        // If reveal was already triggered before callback was set, trigger it now
        if (introRevealPending && callback) {
                introRevealPending = false;
                callback();
        }
};

export const triggerIntroReveal = () => {
        
        if (introRevealCallback) {
                introRevealCallback();
                introRevealCallback = null; // Only trigger once
        } else {
                // Callback not set yet, mark as pending
                introRevealPending = true;
        }
};

// GlobalMask component - persists across all page transitions
function GlobalMask() {
        const maskRef = useRef(null);

        useEffect(() => {
                if (maskRef.current) {
                        setGlobalMaskRef(maskRef.current);
                }
                return () => {
                        setGlobalMaskRef(null);
                };
        }, []);

        const maskStyle = {
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,  // Start covering the screen for first load
                background: Theme.brandAccent,
                // zIndex: 2147483647
        };

        return <div ref={maskRef} style={maskStyle} className="mask" role="progressbar" aria-label="Loading page content">
                <div aria-live="polite" aria-atomic="true">
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <path d="M12,1A11,11,0,1,0,23,12,11,11,0,0,0,12,1Zm0,19a8,8,0,1,1,8-8A8,8,0,0,1,12,20Z" fill="red" opacity=".25"/>
                                <path d="M10.14,1.16a11,11,0,0,0-9,8.92A1.59,1.59,0,0,0,2.46,12,1.52,1.52,0,0,0,4.11,10.7a8,8,0,0,1,6.66-6.61A1.42,1.42,0,0,0,12,2.69h0A1.57,1.57,0,0,0,10.14,1.16Z" className="spinner"/>
                        </svg>
                        <span className="sr-only">Loading...</span>
                </div>
        </div>;
}

const routes = [{
        path: '/',
        title: 'Home',
        component: Landing,
        isPrimary: true,
        exact: true
}, {
        path: '/work',
        title: 'Work',
        component: Work,
        isPrimary: true,
        exact: true
}, {
        path: '/work/:id',
        title: 'Casestudy',
        component: Casestudy,
        isPrimary: false
}, {
        path: '/timeline',
        title: 'Timeline',
        component: Timeline,
        isPrimary: true
}, {
        path: '/about',
        title: 'About',
        component: About,
        isPrimary: true
}]

function AnimatedRoutes({ extras }) {
        const location = useLocation();
        const nodeRef = useRef(null);
        const wrapperRef = useRef(null);
        const pageReadyRef = useRef(false);
        const maskRevealPendingRef = useRef(false);

        const revealMask = () => {
                const mask = getGlobalMask();
                if (!mask) return;
                
                gsap.to(mask, {
                        duration: 0.35,
                        top: '100%',
                        ease: 'power4.easeIn',
                        onComplete: () => {
                                // Reset mask position for next navigation
                                gsap.set(mask, {
                                        top: 0,
                                        bottom: '100%'
                                });
                        }
                });
        };

        const onPageReady = () => {
                pageReadyRef.current = true;
                if (maskRevealPendingRef.current) {
                        // Use double rAF to ensure content is painted
                        requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                        revealMask();
                                });
                        });
                }
        };

        const runLeaveAnimation = () => {
                const mask = getGlobalMask();
                if (!mask) return;
                
                // Reset page ready state for next page
                pageReadyRef.current = false;
                maskRevealPendingRef.current = false;
                
                // Start with mask at top (no height)
                gsap.set(mask, {
                        top: 0,
                        bottom: '100%'
                });

                // Animate mask DOWN to cover the entire viewport
                gsap.to(mask, {
                        duration: 0.35,
                        bottom: 0,
                        ease: 'power4.easeIn'
                });
        };

        const runEnterAnimation = () => {
                const mask = getGlobalMask();
                if (!mask) return;
                
                window.scrollTo(0, 0);
                
                // Mask should be covering the viewport (set by leave animation)
                gsap.set(mask, {
                        top: 0,
                        bottom: 0
                });

                // Show the content (it's behind the mask)
                if (wrapperRef.current) {
                        wrapperRef.current.show();
                }

                // Check if page is already ready (fast load or cached)
                if (pageReadyRef.current) {
                        requestAnimationFrame(() => {
                                requestAnimationFrame(() => {
                                        revealMask();
                                });
                        });
                } else {
                        // Wait for page to signal it's ready
                        maskRevealPendingRef.current = true;
                }
        };
        
        return (
                <main id="main-content" role="main">
                        <SwitchTransition mode="out-in">
                                <Transition
                                        key={location.pathname}
                                        nodeRef={nodeRef}
                                        timeout={{ enter: 400, exit: 400 }}
                                        onExit={() => {
                                                // Called when exiting starts - run leave animation on global mask
                                                runLeaveAnimation();
                                        }}
                                        onEnter={() => {
                                                // Called when entering starts (after exit completes with out-in mode)
                                                if (wrapperRef.current && wrapperRef.current.isIntroComplete()) {
                                                        runEnterAnimation();
                                                }
                                        }}
                                >
                                        <div ref={nodeRef}>
                                                <AnimatedWrapper ref={wrapperRef} location={location}>
                                                        <Routes location={location}>
                                                                {routes.map((item, i) => {
                                                                        const PageComponent = item.component;
                                                                        return (
                                                                                <Route 
                                                                                        key={i} 
                                                                                        path={item.path} 
                                                                                        element={<PageComponent {...extras} {...item} onPageReady={onPageReady} />} 
                                                                                />
                                                                        );
                                                                })}
                                                                <Route path="*" element={<NoMatch {...extras} onPageReady={onPageReady} />} />
                                                        </Routes>
                                                </AnimatedWrapper>
                                        </div>
                                </Transition>
                        </SwitchTransition>
                </main>
        );
}

class App extends Component {

        constructor(props) {

                super(props)

                this.state = {
                        viewport: {
                                width: 0,
                                height: 0
                        },
                        offline: false
                }

                this.resize = this.resize.bind(this)
                this.bind = this.bind.bind(this)
                this.removeBind = this.removeBind.bind(this)

                this.offline = this.offline.bind(this)
                this.online = this.online.bind(this)

        }


        resize() {
                this.setHeight()
        }

        componentWillUnmount() {
                this.removeBind()
        }

        online(){
                this.setState({
                        offline: false
                })
        }
        offline(){
                this.setState({
                        offline: true
                })
        }

        bind() {
                window.addEventListener('resize', this.cb)
                window.addEventListener('online',  this.online)
                window.addEventListener('offline',  this.offline)
        }

        removeBind() {
                window.removeEventListener('resize', this.cb)
                window.removeEventListener('online',  this.online)
                window.removeEventListener('offline',  this.offline)
        }

        setHeight() {

                const viewport = getViewportSize()

                this.setState({
                        viewport
                })

        }




        componentDidMount() {

                if ('scrollRestoration' in window.history) {
                        window.history.scrollRestoration = 'manual';
                }

                this.cb = throttle(this.resize)

                this.setHeight()
                this.bind()

        }


        render() {

                const { viewport, offline } = this.state

                const extras = {
                        viewport: viewport,
                        offline: offline
                }


                return (
                        <Router>
                                <div>
                                        
                                        <CustomCursor />
                                        <Header routes={routes} />

                                        <AnimatedRoutes extras={extras} />

                                        <Social />

                                        <GlobalMask />

                                </div>
                        </Router>
                )
        }
}

export default App
